import { IconTable } from "@tabler/icons-react";
import type {
  ColDef,
  GridApi,
  GridReadyEvent,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { DateTime } from "luxon";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { WarningAlert } from "~/components/Alert";
import { AgGridContainer } from "~/components/domain/reporting/ag-grid/components/AgGridContainer";
import { getNumericColumnConfig } from "~/components/domain/reporting/ag-grid/utils/cell-renderer-utils";
import { useOptionalOrg } from "~/contexts/orgProvider";
import type { BoundFormatMoney } from "~/modules/multi-currency/ui/format-money";
import { useFormatMoney } from "~/modules/multi-currency/ui/format-money";
import { GRAY_PALETTE } from "~/styles/palettes";
import { Box, Flex, SimpleGrid, Text } from "~/ui";
import type { ActiveReportArtifact, ReportFieldDefinition, ReportRow } from "~/utils/types/assistant-report-artifact";

import { AssistantMoneyCellRenderer } from "./AssistantMoneyCellRenderer";
import classes from "./AssistantResults.module.css";
import { EntityDrillCellRenderer } from "./EntityDrillCellRenderer";
import { reconcileCommittedArtifact, reportArtifactVersionKey, shouldReconcileArtifactVersion } from "./report-column-state";
import {
  compareAssistantDecimalStrings,
  exactAssistantDecimalString,
  formatAssistantDecimalString,
  formatAssistantDollarAmount,
  formatAssistantNumber,
  formatAssistantPercentage,
  formatAssistantRowMoney,
  formatCalendarDay,
  formatInstant,
  formatMonthBucket,
  instantExportHeader,
  instantExportText,
  instantOrgDay,
} from "./report-field-formatters";
import { buildReportWorkbook, XLSX_MIME_TYPE } from "./report-workbook";

export interface AssistantResultsHandle {
  download: (format: "xlsx" | "csv") => Promise<void>;
}

interface AssistantResultsProps {
  artifact: ActiveReportArtifact | null;
  visible: boolean;
}

export const buildDefaultColumnDefinition = (interactive: boolean): ColDef<ReportRow> => ({
  sortable: interactive,
  filter: interactive,
  resizable: true,
  flex: undefined,
});

// One object per mode for the panel grids: the grid rebuilds its columns when this reference changes,
// which would drop a sort the person applied whenever the panel re-renders (an export does).
const PANEL_DEFAULT_COLUMN_DEFINITIONS = {
  interactive: { ...buildDefaultColumnDefinition(true), flex: 1 },
  locked: { ...buildDefaultColumnDefinition(false), flex: 1 },
};

export const panelDefaultColumnDefinition = (interactive: boolean): ColDef<ReportRow> =>
  interactive ? PANEL_DEFAULT_COLUMN_DEFINITIONS.interactive : PANEL_DEFAULT_COLUMN_DEFINITIONS.locked;

const csvCell = (value: unknown): string => {
  const rendered = String(value ?? "");
  const formulaSafe = typeof value === "string" && /^[=+\-@\t\r]/.test(value) ? `'${rendered}` : rendered;
  return `"${formulaSafe.replaceAll('"', '""')}"`;
};

const fieldsByKey = (artifact: ActiveReportArtifact): Map<string, ReportFieldDefinition> =>
  new Map(artifact.dataset.fields.map(field => [field.key, field]));

const resolveFields = (fieldKeys: readonly string[], byKey: Map<string, ReportFieldDefinition>): ReportFieldDefinition[] =>
  fieldKeys.flatMap(fieldKey => {
    const field = byKey.get(fieldKey);
    return field ? [field] : [];
  });

/**
 * A money column means nothing without the currency it is denominated in, and currency lives in a
 * sibling reference field. Reference fields stay out of exports, so each money column brings its
 * own currency column with it; otherwise a mixed CAD and USD export reads as one dollar column.
 */
const withCurrencyColumns = (fields: ReportFieldDefinition[], byKey: Map<string, ReportFieldDefinition>): ReportFieldDefinition[] => {
  const exported: ReportFieldDefinition[] = [];
  const included = new Set<string>();
  const include = (field: ReportFieldDefinition | undefined) => {
    if (field && !included.has(field.key)) {
      exported.push(field);
      included.add(field.key);
    }
  };
  for (const field of fields) {
    include(field);
    include(field.currencyCodeKey ? byKey.get(field.currencyCodeKey) : undefined);
  }
  return exported;
};

/** A locked calculation exports every published non-reference field in its published order. */
const lockedExportFields = (artifact: ActiveReportArtifact): ReportFieldDefinition[] => {
  const allowedFieldKeys = new Set(
    artifact.dataset.presentationManifest?.allowedFieldKeys ?? artifact.dataset.fields.map(field => field.key),
  );
  return withCurrencyColumns(
    artifact.dataset.fields.filter(field => field.role !== "reference" && allowedFieldKeys.has(field.key)),
    fieldsByKey(artifact),
  );
};

export type ReportExportBlock =
  | { kind: "pairs"; fields: ReportFieldDefinition[]; row: ReportRow }
  | { kind: "table"; fields: ReportFieldDefinition[]; rows: readonly ReportRow[] };

/** A currency column carries information only when the rows hold more than one currency or a non-base one (the showsCurrency test). */
const currencyIsInformative = (fields: ReportFieldDefinition[], rows: readonly ReportRow[], orgCurrencyCode: string): boolean => {
  const codes = new Set<string>();
  for (const field of fields) {
    if (field.semanticType !== "money" || !field.currencyCodeKey) {
      continue;
    }
    for (const row of rows) {
      const code = row[field.currencyCodeKey];
      if (typeof code === "string" && code.trim()) {
        codes.add(code.trim().toUpperCase());
      }
    }
  }
  return codes.size > 1 || (codes.size === 1 && !codes.has(orgCurrencyCode));
};

/** What an export holds, whatever the format: CSV and Excel are two writers over these blocks. */
export const reportExportBlocks = (
  artifact: ActiveReportArtifact,
  view: {
    tableColumnKeys?: readonly string[];
    tableRows?: readonly ReportRow[];
    lineRows?: readonly ReportRow[];
    orgCurrencyCode?: string;
  } = {},
): ReportExportBlock[] => {
  const byKey = fieldsByKey(artifact);
  if (artifact.presentation.mode === "record") {
    const header: ReportExportBlock = {
      kind: "pairs",
      fields: withCurrencyColumns(resolveFields(artifact.presentation.fieldKeys, byKey), byKey),
      row: artifact.dataset.rows[0] ?? {},
    };
    const lineFields = withCurrencyColumns(recordLineFields(artifact), byKey);
    const lineRows = view.lineRows ?? recordLineRows(artifact);
    return lineFields.length > 0 && lineRows.length > 0 ? [header, { kind: "table", fields: lineFields, rows: lineRows }] : [header];
  }
  if (artifact.dataset.presentationManifest?.rowInteraction?.mode === "locked") {
    return [{ kind: "table", fields: lockedExportFields(artifact), rows: artifact.dataset.rows }];
  }
  // Excel only: the plain-table CSV comes from AG Grid. With no org currency, keep the column, as showsCurrency does.
  const fields = resolveFields(view.tableColumnKeys ?? artifact.presentation.fieldKeys, byKey);
  const rows = view.tableRows ?? artifact.dataset.rows;
  const keepCurrency = view.orgCurrencyCode === undefined || currencyIsInformative(fields, rows, view.orgCurrencyCode);
  return [{ kind: "table", fields: keepCurrency ? withCurrencyColumns(fields, byKey) : fields, rows }];
};

/** Files are named after the report on its as-of day, else the org's calendar day, as the trial balance and work orders exports are. */
export const reportFileStem = (title: string, day: string): string => {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/, "");
  return `${slug || "assistant-report"}-${day}`;
};

/** Pairs leave as label and value rows, a table as its label row then its value rows, with an empty line between blocks. */
const csvFromBlocks = (blocks: readonly ReportExportBlock[], timeZone: string): string => {
  const label = (field: ReportFieldDefinition) =>
    field.semanticType === "instant" ? instantExportHeader(field.label, timeZone) : field.label;
  const value = (field: ReportFieldDefinition, row: ReportRow) =>
    field.semanticType === "instant" && row[field.key] !== null ? instantExportText(row[field.key], timeZone) : row[field.key];
  const rows = blocks.flatMap((block, index): unknown[][] => {
    const blockRows =
      block.kind === "pairs"
        ? block.fields.map(field => [label(field), value(field, block.row)])
        : [block.fields.map(label), ...block.rows.map(row => block.fields.map(field => value(field, row)))];
    return index > 0 ? [[], ...blockRows] : blockRows;
  });
  return rows.map(row => row.map(csvCell).join(",")).join("\n");
};

export const buildGovernedOrderCsv = (artifact: ActiveReportArtifact): string =>
  csvFromBlocks(
    [{ kind: "table", fields: lockedExportFields(artifact), rows: artifact.dataset.rows }],
    artifact.dataset.methodology.timezone,
  );

/** The repeating evidence rows (order lines, supplier costs) a record-mode report lists under its header. */
export const recordLineFields = (artifact: ActiveReportArtifact): ReportFieldDefinition[] => {
  const recordLines = artifact.dataset.presentationManifest?.recordLines;
  return recordLines ? resolveFields(recordLines.fieldKeys, fieldsByKey(artifact)) : [];
};

export const recordLineRows = (artifact: ActiveReportArtifact): ReportRow[] => {
  const recordLines = artifact.dataset.presentationManifest?.recordLines;
  return recordLines ? artifact.dataset.rows.filter(row => row[recordLines.rowKindKey] !== recordLines.headerRowKind) : [];
};

/**
 * Record-mode export: the header record as label and value pairs, then every line beneath it.
 * Exporting only the header would drop the lines the card is counting. Lines leave in the order the line grid
 * shows them (its committed sort and any user sort), as the table export already does through its grid.
 */
export const buildRecordCsv = (artifact: ActiveReportArtifact, orderedLineRows?: readonly ReportRow[]): string =>
  csvFromBlocks(reportExportBlocks(artifact, { lineRows: orderedLineRows }), artifact.dataset.methodology.timezone);

/**
 * Plain-table CSV through AG Grid. Dates leave as stable values (a day as 2026-11-19, a moment as its org wall time
 * 2026-10-19 22:30 under "Created (ET)"); every other column exports as it always has, honoring its own
 * useValueFormatterForExport flag.
 */
export const plainTableCsvCallbacks = (fields: readonly ReportFieldDefinition[], timeZone: string) => {
  const byKey = new Map(fields.map(field => [field.key, field]));
  return {
    processCellCallback: (params: ProcessCellForExportParams<ReportRow>) => {
      const field = byKey.get(params.column.getColId());
      switch (field?.semanticType) {
        case "instant":
          return params.value === null || params.value === undefined ? "" : instantExportText(params.value, timeZone);
        case "calendarDay":
        case "monthBucket":
          return params.value ?? "";
        default:
          return params.column.getColDef().useValueFormatterForExport === false ? params.value : params.formatValue(params.value);
      }
    },
    processHeaderCallback: (params: ProcessHeaderForExportParams<ReportRow>) => {
      const header = params.api.getDisplayNameForColumn(params.column, "csv") ?? "";
      return byKey.get(params.column.getColId())?.semanticType === "instant" ? instantExportHeader(header, timeZone) : header;
    },
  };
};

const numericColumnConfig = (interactive: boolean) => {
  const config = getNumericColumnConfig();
  return { ...config, filter: interactive ? config.filter : false };
};

export const reportFieldMinWidth = (field: ReportFieldDefinition, width?: { minWidth: number }): number =>
  width?.minWidth ?? (field.role === "dimension" ? 180 : 140);

type CommittedSort = ActiveReportArtifact["presentation"]["sort"];

/** The grid's day filter compares the org calendar day a moment falls on, never the browser's day. */
const instantDayFilterComparator =
  (timeZone: string) =>
  (filterLocalDateAtMidnight: Date, cellValue: unknown): number => {
    const cellDay = instantOrgDay(cellValue, timeZone);
    if (!cellDay) {
      return -1;
    }
    const filterDay = DateTime.fromJSDate(filterLocalDateAtMidnight).toFormat("yyyy-MM-dd");
    return cellDay < filterDay ? -1 : cellDay > filterDay ? 1 : 0;
  };

/** Moments sort by their exact epoch, never by their text. */
export const compareInstants = (left: unknown, right: unknown): number => {
  const leftEpoch = typeof left === "string" ? Date.parse(left) : Number.NaN;
  const rightEpoch = typeof right === "string" ? Date.parse(right) : Number.NaN;
  if (!Number.isFinite(leftEpoch)) {
    return Number.isFinite(rightEpoch) ? -1 : 0;
  }
  if (!Number.isFinite(rightEpoch)) {
    return 1;
  }
  return leftEpoch - rightEpoch;
};

export const buildColumnDefinition = (
  field: ReportFieldDefinition,
  width?: { minWidth: number; maxWidth: number },
  interactive = true,
  readOnlyEmbed = false,
  committedSort?: CommittedSort,
  timeZone = "UTC",
): ColDef<ReportRow> => {
  const minWidth = reportFieldMinWidth(field, width);
  // Every grid (chat card, panel, record lines) takes the committed order from its column definitions.
  const sortsThisColumn = committedSort?.fieldKey === field.key;
  const base: ColDef<ReportRow> = {
    colId: field.key,
    field: field.key,
    headerName: field.label,
    sort: sortsThisColumn ? committedSort.direction : null,
    sortIndex: sortsThisColumn ? 0 : null,
    sortable: interactive,
    filter: interactive,
    resizable: !readOnlyEmbed,
    minWidth,
    ...(readOnlyEmbed ? { width: minWidth } : {}),
    maxWidth: width?.maxWidth,
    tooltipField: width ? field.key : undefined,
  };
  if (field.entityRef && !readOnlyEmbed) {
    return { ...base, cellRenderer: EntityDrillCellRenderer, cellRendererParams: { entityRef: field.entityRef } };
  }
  const semanticType = field.semanticType;
  switch (semanticType) {
    case "money": {
      const moneyParams = {
        ...(field.currencyCodeKey ? { currencyCodeKey: field.currencyCodeKey } : {}),
        ...(field.moneyKind ? { moneyKind: field.moneyKind } : {}),
      };
      return {
        ...base,
        cellRenderer: AssistantMoneyCellRenderer,
        ...(Object.keys(moneyParams).length > 0 ? { cellRendererParams: moneyParams } : {}),
        ...numericColumnConfig(interactive),
      };
    }
    case "percentage":
      return {
        ...base,
        valueFormatter: (params: ValueFormatterParams<ReportRow, unknown>) => formatAssistantPercentage(params.value),
        ...numericColumnConfig(interactive),
      };
    case "number":
      return {
        ...base,
        valueFormatter: (params: ValueFormatterParams<ReportRow, unknown>) => formatAssistantNumber(params.value),
        ...numericColumnConfig(interactive),
      };
    case "decimalString":
      return {
        ...base,
        valueFormatter: (params: ValueFormatterParams<ReportRow, unknown>) => formatAssistantDecimalString(params.value),
        comparator: compareAssistantDecimalStrings,
        tooltipValueGetter: params => exactAssistantDecimalString(params.value),
        useValueFormatterForExport: false,
      };
    case "calendarDay":
      return {
        ...base,
        valueFormatter: (params: ValueFormatterParams<ReportRow, unknown>) => formatCalendarDay(params.value),
        useValueFormatterForExport: false,
      };
    case "monthBucket":
      return {
        ...base,
        valueFormatter: (params: ValueFormatterParams<ReportRow, unknown>) => formatMonthBucket(params.value),
        useValueFormatterForExport: false,
      };
    case "instant":
      return {
        ...base,
        valueFormatter: (params: ValueFormatterParams<ReportRow, unknown>) => formatInstant(params.value, timeZone),
        comparator: compareInstants,
        // Without this AG Grid infers a dateTimeString column and reads UTC as the browser's local time.
        cellDataType: false,
        ...(interactive ? { filter: "agDateColumnFilter", filterParams: { comparator: instantDayFilterComparator(timeZone) } } : {}),
        useValueFormatterForExport: false,
      };
    case "boolean":
      return {
        ...base,
        valueFormatter: (params: ValueFormatterParams<ReportRow, unknown>) =>
          params.value === true ? "Yes" : params.value === false ? "No" : "",
      };
    case "text":
      return base;
    default: {
      const unhandled: never = semanticType;
      throw new Error(`Unhandled report field type: ${String(unhandled)}`);
    }
  }
};

const EmptyState = () => (
  <Flex direction="column" align="center" justify="center" h="100%" gap="lg" py="xl">
    <IconTable size={36} stroke={1.5} color={GRAY_PALETTE[4]} />
    <Flex direction="column" align="center" gap="xs">
      <Text size="lg" fw={500} c="gray.6">
        Optional report canvas
      </Text>
      <Text size="sm" c="dimmed" ta="center" maw={340}>
        Your answer is complete in the conversation. When a table adds value, it appears here for sorting, links, calculation details, and
        download.
      </Text>
    </Flex>
  </Flex>
);

const FILTER_PROVENANCE_NOTE = /^\d+ rows? filtered out as requested\.$/;

export const datasetAlertWarnings = (warnings: readonly string[]): string[] =>
  warnings.filter(warning => !FILTER_PROVENANCE_NOTE.test(warning));

export const DatasetWarnings = ({ warnings }: { warnings: readonly string[] }) => {
  const alertWarnings = datasetAlertWarnings(warnings);
  return alertWarnings.length > 0 ? (
    <Box mb="md" role="status" aria-live="polite">
      <WarningAlert>
        {alertWarnings.map(warning => (
          <Text key={warning} size="sm">
            {warning}
          </Text>
        ))}
      </WarningAlert>
    </Box>
  ) : null;
};

const formatRecordValue = (
  field: ReportFieldDefinition,
  value: unknown,
  row: ReportRow,
  formatOrgMoney: BoundFormatMoney,
  timeZone: string,
): string => {
  if (value === null || value === undefined) {
    return "—";
  }
  const semanticType = field.semanticType;
  switch (semanticType) {
    case "percentage":
      return formatAssistantPercentage(value) || "—";
    case "money":
      if (field.currencyCodeKey) {
        return formatAssistantRowMoney(value, row[field.currencyCodeKey], field.moneyKind) || "—";
      }
      return formatAssistantDollarAmount(value, formatOrgMoney, field.moneyKind) || "—";
    case "number":
      return formatAssistantNumber(value) || "—";
    case "decimalString":
      return formatAssistantDecimalString(value) || "—";
    case "calendarDay":
      return formatCalendarDay(value) || "—";
    case "monthBucket":
      return formatMonthBucket(value) || "—";
    case "instant":
      return formatInstant(value, timeZone) || "—";
    case "boolean":
      return value === true ? "Yes" : value === false ? "No" : "—";
    case "text":
      return String(value);
    default: {
      const unhandled: never = semanticType;
      throw new Error(`Unhandled report field type: ${String(unhandled)}`);
    }
  }
};

const downloadFile = (blob: Blob, fileName: string): void => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

const liveGrid = (api: GridApi<ReportRow> | null) => (api && !api.isDestroyed() ? api : null);

export type AssistantArtifactExportPlan = { kind: "download"; csv: string; fileName: string } | { kind: "grid"; fileName: string };

export const buildAssistantArtifactExportPlan = (
  artifact: ActiveReportArtifact,
  day: string,
  orderedLineRows?: readonly ReportRow[],
): AssistantArtifactExportPlan => {
  const fileName = `${reportFileStem(artifact.presentation.title, day)}.csv`;
  if (artifact.presentation.mode === "record") {
    return { kind: "download", csv: buildRecordCsv(artifact, orderedLineRows), fileName };
  }
  if (artifact.dataset.presentationManifest?.rowInteraction?.mode === "locked") {
    return { kind: "download", csv: buildGovernedOrderCsv(artifact), fileName };
  }
  return { kind: "grid", fileName };
};

/** Keep a short line list compact while a long one still fits a readable window. */
export const recordLineGridHeight = (lineCount: number): string => `${Math.min(Math.max(lineCount, 3), 12) * 32 + 44}px`;

export const selectedReportFields = (artifact: ActiveReportArtifact): ReportFieldDefinition[] => {
  const byKey = fieldsByKey(artifact);
  return resolveFields(artifact.presentation.fieldKeys, byKey);
};

export const reportColumnDefinitions = (artifact: ActiveReportArtifact, interactive: boolean, readOnlyEmbed = false): ColDef<ReportRow>[] =>
  selectedReportFields(artifact).map(field =>
    buildColumnDefinition(
      field,
      artifact.dataset.presentationManifest?.fieldWidths[field.key],
      interactive,
      readOnlyEmbed,
      artifact.presentation.sort,
      artifact.dataset.methodology.timezone,
    ),
  );

export const recordLineColumnDefinitions = (
  artifact: ActiveReportArtifact,
  interactive: boolean,
  readOnlyEmbed = false,
): ColDef<ReportRow>[] => {
  const sort = artifact.presentation.sort;
  // A committed sort orders the lines only when it names a line column.
  const lineSort = sort && artifact.dataset.presentationManifest?.recordLines?.fieldKeys.includes(sort.fieldKey) ? sort : undefined;
  return recordLineFields(artifact).map(field =>
    buildColumnDefinition(
      field,
      artifact.dataset.presentationManifest?.fieldWidths[field.key],
      interactive,
      readOnlyEmbed,
      lineSort,
      artifact.dataset.methodology.timezone,
    ),
  );
};

/** The line grid's rows in its displayed order, for exports that follow what the person sees. */
export const gridRowsInDisplayOrder = (api: Pick<GridApi<ReportRow>, "forEachNodeAfterFilterAndSort">): ReportRow[] => {
  const rows: ReportRow[] = [];
  api.forEachNodeAfterFilterAndSort(node => {
    if (node.data) {
      rows.push(node.data);
    }
  });
  return rows;
};

export const shouldReconcileVisibleArtifact = (
  visible: boolean,
  reconciledVersion: string | null,
  artifact: ActiveReportArtifact,
): boolean => visible && shouldReconcileArtifactVersion(reconciledVersion, artifact);

export const AssistantResults = forwardRef<AssistantResultsHandle, AssistantResultsProps>(({ artifact, visible }, ref) => {
  const formatOrgMoney = useFormatMoney();
  const org = useOptionalOrg();
  const orgTimezone = org?.orgTimezone ?? "UTC";
  const orgCurrencyCode = org?.orgBaseCurrencyCode ?? "USD";
  const gridApiRef = useRef<GridApi<ReportRow> | null>(null);
  const lineGridApiRef = useRef<GridApi<ReportRow> | null>(null);
  const reconciledVersionRef = useRef<string | null>(null);
  const rowInteractionLocked = artifact?.dataset.presentationManifest?.rowInteraction?.mode === "locked";

  useImperativeHandle(
    ref,
    () => ({
      download: async format => {
        if (!artifact) {
          return;
        }
        const day = artifact.dataset.freshness?.asOf ?? DateTime.now().setZone(orgTimezone).toISODate() ?? "report";
        if (format === "csv") {
          const plan = buildAssistantArtifactExportPlan(
            artifact,
            day,
            lineGridApiRef.current ? gridRowsInDisplayOrder(lineGridApiRef.current) : undefined,
          );
          if (plan.kind === "download") {
            downloadFile(new Blob([plan.csv], { type: "text/csv;charset=utf-8" }), plan.fileName);
            return;
          }
          gridApiRef.current?.exportDataAsCsv({
            fileName: plan.fileName,
            ...plainTableCsvCallbacks(artifact.dataset.fields, artifact.dataset.methodology.timezone),
          });
          return;
        }
        // A grid ref outlives its grid (a record report replaces a table), and a destroyed grid reads as empty.
        const lineGrid = liveGrid(lineGridApiRef.current);
        const tableGrid = artifact.presentation.mode === "record" ? null : liveGrid(gridApiRef.current);
        const blocks = reportExportBlocks(artifact, {
          lineRows: lineGrid ? gridRowsInDisplayOrder(lineGrid) : undefined,
          tableColumnKeys: tableGrid?.getAllDisplayedColumns().map(column => column.getColId()),
          tableRows: tableGrid ? gridRowsInDisplayOrder(tableGrid) : undefined,
          orgCurrencyCode,
        });
        const workbook = await buildReportWorkbook(blocks, {
          sheetName: artifact.presentation.title,
          orgCurrencyCode,
          timeZone: artifact.dataset.methodology.timezone,
          columnWidthPx: field => reportFieldMinWidth(field, artifact.dataset.presentationManifest?.fieldWidths[field.key]),
        });
        downloadFile(new Blob([workbook], { type: XLSX_MIME_TYPE }), `${reportFileStem(artifact.presentation.title, day)}.xlsx`);
      },
    }),
    [artifact, orgCurrencyCode, orgTimezone],
  );

  const selectedFields = useMemo(() => (artifact ? selectedReportFields(artifact) : []), [artifact]);

  const columnDefs = useMemo(
    () => (artifact ? reportColumnDefinitions(artifact, !rowInteractionLocked) : []),
    [artifact, rowInteractionLocked],
  );
  const lineRows = useMemo(() => (artifact ? recordLineRows(artifact) : []), [artifact]);
  const lineColumnDefs = useMemo(() => (artifact ? recordLineColumnDefinitions(artifact, true) : []), [artifact]);
  const versionKey = artifact ? reportArtifactVersionKey(artifact) : "empty";

  const reconcile = useCallback((api: GridApi<ReportRow>, nextArtifact: ActiveReportArtifact) => {
    reconcileCommittedArtifact(api, nextArtifact);
    reconciledVersionRef.current = reportArtifactVersionKey(nextArtifact);
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<ReportRow>) => {
      gridApiRef.current = params.api;
      if (artifact && visible) {
        reconcile(params.api, artifact);
      }
    },
    [artifact, reconcile, visible],
  );

  useEffect(() => {
    if (!artifact || !gridApiRef.current || !shouldReconcileVisibleArtifact(visible, reconciledVersionRef.current, artifact)) {
      return;
    }
    reconcile(gridApiRef.current, artifact);
  }, [artifact, reconcile, versionKey, visible]);

  if (!artifact) {
    return (
      <Box h="100%" style={{ flex: 1 }}>
        <EmptyState />
      </Box>
    );
  }

  if (artifact.dataset.rowCount === 0) {
    const emptyState = artifact.dataset.presentationManifest?.emptyState ?? {
      title: "Nothing matched",
      description: "Nothing matched what you asked for. Try a wider date range, or remove a filter.",
    };
    return (
      <Box h="100%" py="xl">
        <DatasetWarnings warnings={artifact.dataset.warnings} />
        <Flex direction="column" align="center" justify="center" gap="xs">
          <Text size="lg" fw={600} c="gray.7">
            {emptyState.title}
          </Text>
          <Text size="sm" c="gray.6">
            {emptyState.description}
          </Text>
          <Text size="xs" c="gray.5">
            Nothing to export
          </Text>
        </Flex>
      </Box>
    );
  }

  if (artifact.presentation.mode === "record") {
    const row = artifact.dataset.rows[0] ?? {};
    const recordLines = lineColumnDefs.length > 0 && lineRows.length > 0 ? artifact.dataset.presentationManifest?.recordLines : undefined;
    return (
      <Box style={{ flex: 1, minHeight: 300 }}>
        <DatasetWarnings warnings={artifact.dataset.warnings} />
        <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
          {selectedFields.map(field => (
            <Box key={field.key} p="md" style={{ border: `1px solid ${GRAY_PALETTE[3]}`, borderRadius: 8 }}>
              <Text size="xs" tt="uppercase" c="dimmed" fw={600}>
                {field.label}
              </Text>
              <Text mt={4} size="sm" fw={500}>
                {formatRecordValue(field, row[field.key], row, formatOrgMoney, artifact.dataset.methodology.timezone)}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
        {recordLines && (
          <Box className={classes.gridContainer} mt="lg">
            <Text size="sm" fw={600} c="gray.7" mb="xs">
              {recordLines.title}
            </Text>
            <AgGridContainer
              columnDefs={lineColumnDefs}
              columnDefsVersionKey={`${versionKey}:lines`}
              rowData={lineRows}
              onGridReady={(params: GridReadyEvent<ReportRow>) => {
                lineGridApiRef.current = params.api;
              }}
              height={recordLineGridHeight(lineRows.length)}
              gridTheme="ag-theme-balham"
              defaultColDef={panelDefaultColumnDefinition(true)}
              pagination={lineRows.length > 100}
              paginationPageSize={100}
              suppressRowClickSelection
              suppressContextMenu
              animateRows
              suppressHorizontalScroll={false}
              allowHorizontalOverflow
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box className={classes.gridContainer} style={{ flex: 1, minHeight: 300 }}>
      <DatasetWarnings warnings={artifact.dataset.warnings} />
      {rowInteractionLocked && (
        <Text size="xs" c="dimmed" mb="xs" role="status">
          Sorting and filtering are locked because the published order is part of this calculation.
        </Text>
      )}
      <AgGridContainer
        columnDefs={columnDefs}
        columnDefsVersionKey={versionKey}
        rowData={artifact.dataset.rows}
        onGridReady={onGridReady}
        height="100%"
        gridTheme="ag-theme-balham"
        // flex fills the pane; per-column minWidth still floors widths, so narrow panes scroll
        defaultColDef={panelDefaultColumnDefinition(!rowInteractionLocked)}
        pagination={artifact.dataset.rowCount > 100}
        paginationPageSize={100}
        suppressRowClickSelection
        suppressContextMenu
        animateRows
        suppressHorizontalScroll={false}
        allowHorizontalOverflow
      />
    </Box>
  );
});

AssistantResults.displayName = "AssistantResults";
